// The office's open sheets follow the live events as the board and Calls do (QA-728): a job sheet
// or an invoice sheet left open while the customer signs or pays reads itself again. The stream names
// each change's record (its type and id), so a sheet follows the records it shows; money is named by
// its payment, not the invoice it settles, so any money event reads an open sheet again. A text or
// email coming in or going out is named by its own new message, which the page hasn't read yet, so a
// new message reads an open job sheet again too (QA-743): the customer's reply shows as it arrives.
// A change that names its job (`jobId`) reads only that job's page; one that names none, as every
// message event does today (the stream sends its type and record only), reads every open job page,
// once for a burst (QA-744).
import { onLiveChange, type LiveChange } from '../connection/live.ts';

/** Money moving: a payment, a refund, a card charge, a credit, a deposit, a credit note. */
const MONEY_EVENTS = /^(payment|refund|card|customer_credit|deposit|credit_note)\./;
/** A text or email new to a job: a reply coming in, one queued to go out, one the office matched to a job. */
const NEW_MESSAGES = /^message\.(received|queued|assigned)$/;
/**
 * What a job sheet shows: the job and its visits, its estimates and its invoices, its texts and emails
 * (one sent, delivered or failed), and the thread with its techs (named by the job).
 */
const JOB_RECORDS = /^(job|job_message|appointment|estimate|invoice|message)\./;

/** Whether an invoice sheet reads itself again after this change. */
export function invoiceSheetFollows(c: LiveChange, invoiceId: string): boolean {
  if (MONEY_EVENTS.test(c.type)) return true;
  return /^invoice\./.test(c.type) && c.entityId === invoiceId;
}

/** The records a job sheet shows, by id: the job, its visits, estimates, invoices and messages. */
export type JobSheetRecords = ReadonlySet<string>;

/** Whether a job sheet reads itself again after this change. */
export function jobSheetFollows(c: LiveChange, records: JobSheetRecords): boolean {
  if (MONEY_EVENTS.test(c.type)) return true;
  // a new text or email: its own job's page when it names one, else any open one (QA-744)
  if (NEW_MESSAGES.test(c.type)) return c.jobId ? records.has(c.jobId) : true;
  return JOB_RECORDS.test(c.type) && !!c.entityId && records.has(c.entityId);
}

/** A job page waits this long after a change it follows, so a burst of them is one read (QA-744). */
export const JOB_PAGE_WAIT_MS = 500;

/**
 * While a sheet is open: read it again when `follows` says a change is its, once for a burst of changes:
 * the first starts the wait (`waitMs`, 250 ms), and those during it join that one read, so a steady
 * stream of them reads it at most once a wait, never not at all. The returned function stops.
 */
export function followLive(
  follows: (c: LiveChange) => boolean,
  reload: () => void,
  waitMs = 250,
): () => void {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const stop = onLiveChange((c) => {
    if (!follows(c) || timer !== undefined) return;
    timer = setTimeout(() => {
      timer = undefined;
      reload();
    }, waitMs);
  });
  return () => {
    clearTimeout(timer);
    timer = undefined;
    stop();
  };
}
